import type { ReactNode } from "react";
import { Tag } from "primereact/tag";

import { Dialog, Icon, StateMessage } from "@/ds";
import type { IconName } from "@/ds";
import { UserAvatarByEmail } from "@/shared/components/user-avatar-by-email";
import { AUDIT_DETAIL_EMPTY_FIELD, AUDIT_DETAIL_TITLE } from "./audit-detail-dialog.constants";
import styles from "./audit-detail-dialog.module.scss";
import type { AuditDetailDialogViewModel } from "./types-audit-detail-dialog";

interface CampoProps {
  readonly label: string;
  readonly testId: string;
  /** Coluna do campo no card: Ação e Justificativa à esquerda, Valor Anterior e Novo Valor à direita; `completa` ocupa a largura toda. */
  readonly coluna?: "esquerda" | "direita" | "completa";
  readonly children: ReactNode;
}

function Campo({ label, testId, coluna = "esquerda", children }: CampoProps) {
  return (
    <div className={styles.campo} data-coluna={coluna}>
      <dt className={styles.label}>{label}</dt>
      <dd className={styles.valor} data-testid={testId}>
        {children}
      </dd>
    </div>
  );
}

interface ContatoProps {
  readonly icon: IconName;
  readonly label: string;
  readonly testId: string;
  readonly children: ReactNode;
}

/** Linha de contato do responsável: ícone à esquerda e o valor ao lado. */
function Contato({ icon, label, testId, children }: ContatoProps) {
  return (
    <div className={styles.contato}>
      <dt className={styles.contatoIcone}>
        <Icon name={icon} size={14} title={label} />
      </dt>
      <dd className={styles.contatoValor} data-testid={testId}>
        {children}
      </dd>
    </div>
  );
}

interface LinhasProps {
  readonly linhas: ReadonlyArray<{
    readonly chave?: string;
    readonly rotulo: string;
    readonly anterior?: string | null;
    readonly valor: string;
  }>;
}

/** Grade `Variável: valor`; no que mudou, o antes riscado, a seta e o depois. */
function Linhas({ linhas }: LinhasProps) {
  return (
    <span className={styles.atributos}>
      {linhas.map(({ chave, rotulo, anterior, valor }, indice) => (
        <span key={`${indice}:${chave ?? rotulo}`} className={styles.atributo}>
          <span className={styles.atributoRotulo}>{rotulo}</span>
          <span className={styles.atributoValor}>
            {anterior ? (
              <>
                <span className={styles.riscado}>{anterior}</span>
                <Icon name="arrow-right" size={12} className={styles.seta} />
              </>
            ) : null}
            <span className={styles.atributoTexto}>{valor}</span>
          </span>
        </span>
      ))}
    </span>
  );
}

/** Modal Detalhes da Auditoria (Figma 4945:60029): card com a alteração, o responsável e o evento. */
export function AuditDetailDialogView({ estado, campos, fechar }: AuditDetailDialogViewModel) {
  return (
    <Dialog
      visible
      onHide={fechar}
      className={styles.dialog}
      header={AUDIT_DETAIL_TITLE}
      pt={{ headerTitle: { className: "p-dialog-title-sm" } }}
      dismissableMask
      data-testid="audit-detail-dialog"
    >
      {estado ? (
        <StateMessage
          variant={estado.variant}
          title={estado.title}
          action={estado.action}
          inline
          testId={estado.testId}
        />
      ) : null}

      {campos ? (
        <div className={styles.card} data-testid="audit-detail-fields">
          <dl className={styles.grade}>
            <Campo label="Ação" testId="audit-detail-acao">
              <span className={styles.acao}>{campos.acao}</span>
            </Campo>

            {campos.valorAnterior ? (
              <Campo label="Valor Anterior" testId="audit-detail-valor-anterior" coluna="direita">
                <span
                  className={campos.valorAnteriorRiscado ? styles.riscado : undefined}
                  data-testid={campos.valorAnteriorRiscado ? "audit-detail-riscado" : undefined}
                >
                  {campos.valorAnterior}
                </span>
              </Campo>
            ) : null}

            {campos.detalhes ? (
              <Campo label="Detalhes" testId="audit-detail-detalhes" coluna="completa">
                <Linhas linhas={campos.detalhes} />
              </Campo>
            ) : null}

            {campos.valorNovoAtributos ? (
              <Campo label="Novo Valor" testId="audit-detail-valor-novo" coluna="completa">
                <Linhas linhas={campos.valorNovoAtributos} />
              </Campo>
            ) : null}

            {campos.registroAlterado ? (
              <Campo label={campos.registroAlterado.label} testId="audit-detail-registro-alterado">
                {campos.registroAlterado.valor}
              </Campo>
            ) : null}

            {campos.obra ? (
              <Campo label="Obra" testId="audit-detail-registro-obra">
                {campos.obra}
              </Campo>
            ) : null}

            {campos.justificativa ? (
              <Campo label="Justificativa" testId="audit-detail-justificativa">
                {campos.justificativaPares ? (
                  <Linhas linhas={campos.justificativaPares} />
                ) : (
                  campos.justificativa
                )}
              </Campo>
            ) : null}

            {!campos.valorNovoAtributos && campos.valorNovo ? (
              <Campo label="Novo Valor" testId="audit-detail-valor-novo" coluna="direita">
                {campos.valorNovo}
              </Campo>
            ) : null}
          </dl>

          <hr className={styles.divisor} />

          <div className={styles.rodape}>
            <div className={styles.responsavel} data-testid="audit-detail-usuario">
              <p className={styles.label}>Responsável</p>

              <div className={styles.pessoa}>
                <UserAvatarByEmail
                  className="p-avatar-56"
                  nome={campos.responsavel.nome}
                  email={campos.responsavel.emailDaFoto}
                />

                <div className={styles.pessoaInfo}>
                  <p className={styles.pessoaNome}>{campos.responsavel.nome}</p>

                  <dl className={styles.contatos}>
                    {campos.responsavel.email ? (
                      <Contato icon="envelope" label="E-mail" testId="audit-detail-usuario-email">
                        {campos.responsavel.email}
                      </Contato>
                    ) : null}
                    {campos.responsavel.telefone ? (
                      <Contato icon="phone" label="Telefone" testId="audit-detail-usuario-telefone">
                        {campos.responsavel.telefone}
                      </Contato>
                    ) : null}
                    {campos.responsavel.cargo ? (
                      <Contato icon="users" label="Cargo" testId="audit-detail-usuario-cargo">
                        {campos.responsavel.cargo}
                      </Contato>
                    ) : null}
                  </dl>
                </div>
              </div>
            </div>

            <dl className={styles.evento}>
              <Campo label="Data / Hora" testId="audit-detail-data-hora" coluna="direita">
                {campos.dataHora}
              </Campo>

              <div className={styles.modulo}>
                <dt className={styles.label}>Módulo</dt>
                <dd className={styles.valor} data-testid="audit-detail-modulo">
                  {campos.modulo ? (
                    <Tag severity="secondary" value={campos.modulo} />
                  ) : (
                    AUDIT_DETAIL_EMPTY_FIELD
                  )}
                </dd>
              </div>
            </dl>
          </div>
        </div>
      ) : null}
    </Dialog>
  );
}
